import React, { useMemo } from 'react';
import { Stack, Cluster, Grid, Text, List, Icon, Progress } from '@iqueue/iq-ui';
import Section, { Empty } from './Section';
import { useUsageData, useSlowWait } from './adminData';
import { agentInfo } from '../../lib/agentRegistry';

/**
 * Claude usage dashboard (admin). Две секции того же экрана: цифры на плите и
 * список по агентам (см. ./Section). Card'ов здесь нет намеренно — `filled` Card
 * пиннит тир 4, а это лист лестницы: всё внутри перестаёт выводить тон.
 *
 * Графики — сырой SVG: chart-примитива в библиотеке пока нет
 * (docs/IQ_UI_CHAT_GAPS.md §10.9). Один hue темы, тонкие бары со скруглённым
 * верхом на волосяной базовой линии, нативные подсказки по ховеру, muted-оси.
 */

const fmtCost = (v) => `$${(v || 0).toFixed(v >= 10 ? 0 : 2)}`;
const fmtTok = (v) => v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}k` : String(v || 0);

/** Thin top-rounded bars, left-aligned in fixed slots, capped width. */
function UsageBars({ data, metric, ariaLabel }) {
  const W = 560, H = 120, LABEL_H = 14, TOP = 16;
  const plotH = H - LABEL_H - TOP;
  const rows = data.map(d => ({
    label: d.label,
    value: metric === 'cost' ? d.cost_usd : (d.input_tokens + d.output_tokens),
    runs: d.runs,
    hint: d.hint,
  }));
  const max = Math.max(...rows.map(d => d.value), 1e-9);
  const maxIdx = rows.findIndex(d => d.value === max);
  const n = rows.length;
  const gap = 2;
  const slot = W / Math.max(n, 12);
  const bw = Math.max(3, Math.min(40, slot - gap));
  const fmt = metric === 'cost' ? fmtCost : fmtTok;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={ariaLabel} preserveAspectRatio="xMinYMid meet">
      {rows.map((d, i) => {
        const h = Math.max(d.value > 0 ? 3 : 0, (d.value / max) * plotH);
        const x = i * slot;
        const y = TOP + plotH - h;
        const r = Math.min(4, bw / 2, h);
        return (
          <g key={d.label + i}>
            {h > 0 && (
              <path
                d={`M${x},${y + h} v${-(h - r)} q0,${-r} ${r},${-r} h${bw - 2 * r} q${r},0 ${r},${r} v${h - r} z`}
                fill="var(--md-sys-color-primary)"
                opacity={i === n - 1 ? 1 : 0.7}
              >
                <title>{`${d.hint || d.label}: ${fmt(d.value)} · ${d.runs} runs`}</title>
              </path>
            )}
            {i === maxIdx && d.value > 0 && (
              <text x={x + bw / 2} y={y - 4} textAnchor="middle" fontSize="10"
                fill="var(--md-sys-color-on-surface-variant)">{fmt(d.value)}</text>
            )}
            <text x={x + bw / 2} y={H - 2} textAnchor="middle" fontSize="9"
              fill="var(--md-sys-color-outline)">{d.label}</text>
          </g>
        );
      })}
      <line x1="0" x2={Math.max(n * slot, W)} y1={TOP + plotH + 0.5} y2={TOP + plotH + 0.5}
        stroke="var(--md-sys-color-outline-variant)" strokeWidth="1" />
    </svg>
  );
}

/**
 * Итог за период: подпись и цифра, и больше ничего. Третья строка («N runs ·
 * X tok») ушла — на полной ширине две плитки с тремя строками каждая читались
 * одним слипшимся комком, а те же числа лежат ниже: в метрах, в графиках по
 * ховеру и в списке по агентам.
 */
function Total({ label, row, fmt }) {
  return (
    <Stack gap={0}>
      <Text variant="label-small" muted render={<span />}>{label}</Text>
      <Text variant="headline-small" render={<span />}>{fmt(row)}</Text>
    </Stack>
  );
}

/** Rolling-window meter: window cost as a share of the reference (week). */
function WindowMeter({ label, row, refCost, metric }) {
  const value = metric === 'cost' ? row.cost_usd : (row.input_tokens + row.output_tokens);
  const fmt = metric === 'cost' ? fmtCost : fmtTok;
  const pct = refCost > 0 ? Math.round((row.cost_usd / refCost) * 100) : 0;
  return (
    <Stack gap={25}>
      <Cluster justify="between" align="baseline">
        <Text variant="label-small" muted render={<span />}>{label}</Text>
        <Text variant="label-medium" render={<span />}>{fmt(value)} · {row.runs} runs</Text>
      </Cluster>
      <Progress value={pct} aria-label={`${label}: ${fmt(value)}`} />
    </Stack>
  );
}

export default function UsagePanel() {
  // Тот же снапшот вне компонента, что и у панели: возврат на вкладку рисует
  // прошлые цифры сразу, а круг опроса обновляет их на месте.
  const usage = useUsageData();
  const waiting = useSlowWait(!usage);

  const metric = useMemo(() => {
    if (!usage) return 'cost';
    return usage.totals.period.cost_usd > 0 ? 'cost' : 'tokens';
  }, [usage]);

  const dayData = useMemo(() => (usage ? usage.days.map(d => ({ ...d, label: d.day.slice(8), hint: d.day })) : []), [usage]);
  const hourData = useMemo(() => (usage ? usage.hours.map(h => ({ ...h, label: h.hour.slice(11), hint: h.hour.replace('T', ' ') + ':00' })) : []), [usage]);

  // До порога — ничего: мелькнувший на кадр «считаю расход» и есть та резкость.
  if (!usage) return waiting ? <Section><Empty>Считаю расход…</Empty></Section> : null;

  const { totals, agents, windows } = usage;
  const fmt = metric === 'cost' ? fmtCost : fmtTok;
  const val = (row) => metric === 'cost' ? row.cost_usd : (row.input_tokens + row.output_tokens);
  const fmtRow = (row) => fmt(val(row));

  return (
    <>
      <Section plate>
        <Stack gap={300}>
          {/* Сетка, а не Cluster: на широкой плите колонки разносят плитки по
              ширине, тогда как flex-строка сбивает их в комок у левого края. */}
          <Grid min="medium" gap={200}>
            <Total label="Сегодня" row={totals.today} fmt={fmtRow} />
            <Total label="14 дней" row={totals.period} fmt={fmtRow} />
          </Grid>

          <Stack gap={100}>
            <Text variant="label-small" muted render={<span />}>Скользящие окна (доля от недели)</Text>
            <WindowMeter label="За час" row={windows.hour} refCost={windows.week.cost_usd} metric={metric} />
            <WindowMeter label="За сутки" row={windows.day} refCost={windows.week.cost_usd} metric={metric} />
            <WindowMeter label="За неделю" row={windows.week} refCost={windows.week.cost_usd} metric={metric} />
          </Stack>

          <Stack gap={50}>
            <Text variant="label-small" muted render={<span />}>{metric === 'cost' ? 'По часам (24ч), $' : 'По часам (24ч), токены'}</Text>
            <UsageBars data={hourData} metric={metric} ariaLabel="Расход по часам" />
          </Stack>

          <Stack gap={50}>
            <Text variant="label-small" muted render={<span />}>{metric === 'cost' ? 'По дням (14д), $' : 'По дням (14д), токены'}</Text>
            <UsageBars data={dayData} metric={metric} ariaLabel="Расход по дням" />
          </Stack>
        </Stack>
      </Section>

      <Section>
        {agents.length === 0 ? <Empty>Запусков пока не было</Empty> : (
          <List aria-label="Расход по агентам за 30 дней">
            {agents.map(a => (
              <List.Item
                key={a.agent_id}
                leading={<Icon name={agentInfo(a.agent_id).icon} />}
                // Окно (30 дней) переехало в строку: подписи над блоком больше
                // нет, а цифра без периода — просто число.
                supporting={`${a.runs} runs за 30 дней · ${fmtTok(a.input_tokens)} in / ${fmtTok(a.output_tokens)} out`}
                trailing={fmtRow(a)}
              >
                @{a.agent_id}
              </List.Item>
            ))}
          </List>
        )}
      </Section>
    </>
  );
}
